import DOMPurify from 'dompurify';

const CLINICAL_ALLOWED_TAGS = [
  'br', 'b', 'strong', 'i', 'em', 'u', 'p', 'span', 'div',
  'ul', 'ol', 'li', 'small', 'sub', 'sup', 'blockquote', 'hr',
] as const;

const PURIFY = {
  ALLOWED_TAGS: [...CLINICAL_ALLOWED_TAGS],
  ALLOWED_ATTR: [],
};

/** Sanitiza HTML de evoluções importadas (<br>, <b>, etc.) antes de usar em UI ou impressão. */
export function sanitizeClinicalHtml(raw: string | null | undefined): string {
  if (raw == null) return '';
  const s = String(raw);
  if (!s.trim()) return '';

  if (typeof window === 'undefined') {
    return s.replace(/<\s*\/?script[^>]*>/gi, '').replace(/\s*\bon\w+\s*=/gi, ' ');
  }

  return DOMPurify.sanitize(s, PURIFY);
}

/** Sanitiza HTML de documentos (contratos, termos): mantém formatação e tabelas, remove scripts e handlers. */
export function sanitizeDocumentHtml(raw: string | null | undefined): string {
  if (raw == null) return '';
  return DOMPurify.sanitize(String(raw), { USE_PROFILES: { html: true } });
}

/** Indica se o conteúdo parece HTML clínico (vs. texto plano legado). */
export function looksLikeClinicalHtml(raw: string | null | undefined): boolean {
  if (raw == null || !String(raw).trim()) return false;
  return /<(?:br|b|strong|i|em|u|p|div|ul|ol|li)\b/i.test(String(raw));
}

/** Verifica se o corpo clínico está vazio (ignora tags vazias). */
export function clinicalBodyIsEmpty(raw: string | null | undefined): boolean {
  return !clinicalHtmlToPlainPreview(raw, Number.MAX_SAFE_INTEGER).trim();
}

/** Remove rótulo legado "Texto livre:" do corpo (a UI já tem título próprio). */
export function stripEvolutionFreeTextLabel(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim()) return '';
  let s = String(raw);
  s = s.replace(
    /^(\s|<br\s*\/?>)*((?:<[^>]+>\s*)*)Texto\s+livre\s*:?\s*((?:<\/[^>]+>\s*)*)((?:<br\s*\/?>\s*)*)?/i,
    '',
  );
  s = s.replace(/^Texto\s+livre\s*:?\s*/i, '');
  return s;
}

function unescapeDoubleEncodedClinicalHtml(raw: string): string {
  if (!/&lt;(br|b|strong|i|em|u|p|div|ul|ol|li)\b/i.test(raw)) return raw;
  return raw
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/** Quebra blocos clínicos importados em linha única (sem \\n e sem tags HTML). */
function formatPlainClinicalWallText(text: string): string {
  const s = text.trim();
  if (!s || looksLikeClinicalHtml(s) || /\n/.test(s) || s.length < 80) return s;

  const markers = [
    'OD:', 'OE:', 'AO:', 'PIO', 'CD:', 'OCT', 'CCG', 'PANOMAP',
    'BIO:', 'FO:', 'Gonio:', 'Hoje:', 'ARTEFATOS:', 'REPETIR',
    'INICIO', 'ACOMPANHAMENTO', 'CONDUTA:', 'Comorbidade:',
    'Remédios:', 'Antecedentes', 'Pressão intraocular:',
  ];

  let out = s;
  for (const marker of markers) {
    const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(`\\s+(?=${escaped})`, 'gi'), '\n');
  }
  out = out.replace(/\s+-\s+(?=[A-ZÁÉÍÓÚÃÕÇ])/g, '\n');
  return out;
}

/**
 * Normaliza corpo de evolução antes de abrir no editor — import legado, HTML
 * escapado ou rascunho reaberto que chegou como bloco único.
 */
export function prepareEvolutionFreeTextForEditor(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim()) return '';
  let s = unescapeDoubleEncodedClinicalHtml(String(raw));
  s = stripEvolutionFreeTextLabel(s);
  if (!looksLikeClinicalHtml(s)) {
    s = formatPlainClinicalWallText(s);
  }
  return s;
}

/** Converte texto plano ou HTML legado para HTML editável no contentEditable. */
export function toClinicalEditorHtml(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim()) return '';
  const s = String(raw);
  if (looksLikeClinicalHtml(s)) return sanitizeClinicalHtml(s);
  return s
    .split('\n')
    .map((line) => {
      const escaped = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return line ? `<div>${escaped}</div>` : '<div><br></div>';
    })
    .join('');
}

/** Renderiza corpo clínico para impressão (HTML sanitizado ou texto plano com quebras). */
export function renderClinicalBodyForPrint(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim()) return '';
  const s = String(raw);
  if (looksLikeClinicalHtml(s)) return sanitizeClinicalHtml(s);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
}

/** Uma linha de pré-visualização sem tags HTML (listas / resumos na UI). */
export function clinicalHtmlToPlainPreview(raw: string | null | undefined, maxLen = 120): string {
  if (raw == null || !String(raw).trim()) return '';
  const withBreaks = String(raw)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(?:p|div|li|tr|blockquote)>/gi, ' ')
    .replace(/<[^>]+>/g, '');
  const decoded = withBreaks
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#(\d+);/g, (_, n) => {
      const code = Number(n);
      if (!Number.isFinite(code) || code < 1) return '';
      try {
        return String.fromCharCode(code);
      } catch {
        return '';
      }
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => {
      try {
        return String.fromCharCode(parseInt(h, 16));
      } catch {
        return '';
      }
    });
  const oneLine = decoded.replace(/\s+/g, ' ').trim();
  if (maxLen <= 0 || oneLine.length <= maxLen) return oneLine;
  return `${oneLine.slice(0, maxLen)}…`;
}
